/**
 * Pure helpers of the light recording page (UC-8): what the page says for each state of the
 * job, a waveform drawn from the turns, the turn playback is in, and how a voice is shown.
 */
import type { TFunction } from 'i18next'
import {
  isLiveReading,
  type StageReading,
  stageReading,
} from '@/components/processing/processing-stages'
import type { RecordingData, RecordingTurn } from './recording-types'

// ── What the page says ───────────────────────────────────────────────────────

/**
 * What the transcript part of the page shows. Without a stored transcript: `transcribing` while
 * the job runs, `failed` when the job failed before one, `unavailable` when the job finished and
 * the transcript is still not there (it is gone, or cannot be read). `empty`: no speech was
 * found. `ready`: the turns, with a note while the entities are still being extracted
 * (`extracting`) or once that failed (`not_extracted` — the transcript itself is complete).
 */
export type TranscriptPhase =
  | { kind: 'transcribing' }
  | { kind: 'failed' }
  | { kind: 'unavailable' }
  | { kind: 'empty' }
  | { kind: 'ready'; note: 'extracting' | 'not_extracted' | null }

/** What a recording without a stored transcript says, by the state of its job. */
const WITHOUT_TRANSCRIPT = {
  processing: 'transcribing',
  failed: 'failed',
  done: 'unavailable',
} as const

const READY_NOTE = { processing: 'extracting', failed: 'not_extracted', done: null } as const

/** The transcript is stored before the entities are extracted, so each state reads on its own. */
export function transcriptPhase(
  recording: Pick<RecordingData, 'state' | 'transcript'>
): TranscriptPhase {
  const { status } = recording.transcript
  if (status === 'empty') return { kind: 'empty' }
  if (status === 'ready') return { kind: 'ready', note: READY_NOTE[recording.state] }
  return { kind: WITHOUT_TRANSCRIPT[recording.state] }
}

// ── The stages of processing ─────────────────────────────────────────────────

type StagedRecording = Pick<RecordingData, 'state' | 'stage' | 'failedStage' | 'transcript'>

/**
 * The stages the page shows as a chain, or null: a processed recording has nothing left to tell,
 * and a server that names no stage — or a stage that contradicts the job, as a failed job with
 * no stage it failed in — leaves the page to what it said before. A job still `processing` may
 * read as failed: the poll says so before the view is read again.
 */
export function shownStages(
  recording: Pick<StagedRecording, 'state' | 'stage' | 'failedStage'>
): Exclude<StageReading, { kind: 'done' }> | null {
  const reading = stageReading(recording.stage, recording.failedStage)
  if (!isLiveReading(reading) || recording.state === 'done') return null
  if (recording.state === 'failed' && reading.kind !== 'failed') return null
  return reading
}

/** What stands under the heading *Transcript*, below the chain of stages when there is one. */
export type TranscriptBody =
  /** The turns; `note`: the one line about the entities a page without the chain carries. */
  | { kind: 'turns'; note: 'extracting' | 'not_extracted' | null }
  /** A card in place of the turns. */
  | { kind: 'status'; status: Exclude<TranscriptPhase['kind'], 'ready'> }
  /** The rows that are not there yet. */
  | { kind: 'pending' }
  /** Nothing more: the chain has said where the job stopped and what is left of it. */
  | { kind: 'none' }

export interface TranscriptParts {
  /** The chain of stages stands on top. */
  rail: boolean
  body: TranscriptBody
}

/** The body under a chain: what the chain already says is not said a second time. */
function bodyUnderRail(
  phase: TranscriptPhase,
  reading: NonNullable<ReturnType<typeof shownStages>>
): TranscriptBody {
  switch (phase.kind) {
    case 'ready':
      return { kind: 'turns', note: null }
    case 'transcribing':
      return { kind: 'pending' }
    case 'failed':
      // Past the transcript and failed, yet none can be read: that is its own news.
      return reading.at === 'queued' || reading.at === 'transcript'
        ? { kind: 'none' }
        : { kind: 'status', status: 'unavailable' }
    default:
      return { kind: 'status', status: phase.kind }
  }
}

/**
 * The transcript part of the page. Where the server names the stages and the job runs or failed,
 * the chain stands on top and tells what `transcribing`, `extracting`, `failed` and
 * `not_extracted` told before; without it the page shows exactly what it showed before.
 */
export function transcriptParts(recording: StagedRecording): TranscriptParts {
  const phase = transcriptPhase(recording)
  const reading = shownStages(recording)
  if (reading) return { rail: true, body: bodyUnderRail(phase, reading) }
  const body: TranscriptBody =
    phase.kind === 'ready'
      ? { kind: 'turns', note: phase.note }
      : { kind: 'status', status: phase.kind }
  return { rail: false, body }
}

// ── Waveform ─────────────────────────────────────────────────────────────────

/** Bars of the player's waveform. */
export const WAVEFORM_BARS = 90

/**
 * The player's waveform without decoding the audio: for each bar, the share of its time span
 * in which somebody speaks, 0..1. It shows where the speech is, not how loud it was.
 */
export function waveformFromTurns(
  turns: readonly Pick<RecordingTurn, 'startMs' | 'endMs'>[],
  durationMs: number,
  bars: number = WAVEFORM_BARS
): number[] {
  const spoken = Array.from({ length: Math.max(0, bars) }, () => 0)
  if (spoken.length === 0 || durationMs <= 0) return spoken
  const barMs = durationMs / spoken.length
  for (const turn of turns) {
    const start = Math.max(0, turn.startMs)
    const end = Math.min(durationMs, turn.endMs)
    if (end <= start) continue
    for (let bar = Math.floor(start / barMs); bar < spoken.length && bar * barMs < end; bar++) {
      spoken[bar] += Math.min(end, (bar + 1) * barMs) - Math.max(start, bar * barMs)
    }
  }
  // Two voices at once still fill the bar only once.
  return spoken.map(ms => Math.min(1, ms / barMs))
}

// ── The turn being played ────────────────────────────────────────────────────

/** A seek lands a few milliseconds before the time asked for: that is the turn's start still. */
const SEEK_SLACK_MS = 150

/**
 * The turn a playback position belongs to: the last one that has started, so a pause between
 * two turns keeps the one just heard marked. Null before the first turn. `turns` are in time
 * order; a binary search keeps it cheap on every playback tick.
 */
export function turnAt(turns: readonly RecordingTurn[], ms: number): number | null {
  let lo = 0
  let hi = turns.length
  while (lo < hi) {
    const mid = (lo + hi) >>> 1
    if (turns[mid].startMs <= ms + SEEK_SLACK_MS) lo = mid + 1
    else hi = mid
  }
  return lo > 0 ? turns[lo - 1].index : null
}

// ── Voices ───────────────────────────────────────────────────────────────────

export interface VoiceTone {
  dot: string
  text: string
}

/** Data colours for dots and names only, never body text; they repeat from the ninth voice. */
const VOICE_TONES: readonly VoiceTone[] = [
  { dot: 'bg-cat-1', text: 'text-cat-1' },
  { dot: 'bg-cat-4', text: 'text-cat-4' },
  { dot: 'bg-cat-10', text: 'text-cat-10' },
  { dot: 'bg-cat-3', text: 'text-cat-3' },
  { dot: 'bg-cat-7', text: 'text-cat-7' },
  { dot: 'bg-cat-2', text: 'text-cat-2' },
  { dot: 'bg-cat-5', text: 'text-cat-5' },
  { dot: 'bg-cat-8', text: 'text-cat-8' },
]

const UNKNOWN_VOICE_TONE: VoiceTone = { dot: 'bg-muted-foreground', text: 'text-muted-foreground' }

/** The colour of a voice: by its number, muted for a voice the transcription could not tell. */
export function voiceTone(voice: number): VoiceTone {
  return voice > 0 ? VOICE_TONES[(voice - 1) % VOICE_TONES.length] : UNKNOWN_VOICE_TONE
}

/** "Voice 2", or "Unknown voice" for voice 0. A voice has no role yet: nobody confirmed one. */
export function voiceLabel(voice: number, t: TFunction): string {
  return voice > 0
    ? t('humint.questioning.light.page.transcript.voice', { number: voice })
    : t('humint.questioning.light.page.transcript.unknownVoice')
}
